// components/SpineCanvas.tsx
"use client";
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-namespace */

import Link from "next/link";
import { useEffect, useRef, useState } from "react";

import React from 'react'

import { Fragment, useId } from "react"

import {
  checkboxesFeature,
  hotkeysCoreFeature,
  selectionFeature,
  syncDataLoaderFeature,
} from "@headless-tree/core"
import { useTree } from "@headless-tree/react"
import { FileIcon, FolderIcon, FolderOpenIcon } from "lucide-react"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { cn } from "@/lib/utils"

import { ChevronDown, Plus, Minus } from 'lucide-react';

import { Checkbox } from "@/components/ui/checkbox"
import { Tree, TreeItem, TreeItemLabel } from "@/components/ui/tree"

import { spine } from '@/lib/spine-webgl';

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion"; 

interface SpineWebGLProps {
    animation?: string;   // e.g., "/spines/character.atlas"
}

interface Item {
  name: string
  children?: string[]
}
function buildSlotTreeData(slots: string[]): Record<string, Item> {
  const items: Record<string, Item> = {
    root: { name: "Slots", children: [] },
  };

  for (const slotName of slots) {
    const parts = slotName.split("_"); // split by underscores
    let parentId = "root";
    let path = "";

    parts.forEach((part, idx) => {
      path = path ? `${path}_${part}` : part; // unique id
      const isLeaf = idx === parts.length - 1;

      if (!items[path]) {
        items[path] = {
          name: part,
          children: [],
        };
        items[parentId]!.children!.push(path);
      }

      parentId = path;

      if (isLeaf) {
        // If leaf node is already treated as folder (has children),
        // we *also* need a distinct "file node" representing the slot itself.
        // Example: q_head → folder "q/head" + file "q_head"
        if (items[path].children!.length > 0) {
          const fileNodeId = `${path}__file`; // disambiguated id
          items[fileNodeId] = { name: part }; // leaf-only node
          items[parentId].children!.push(fileNodeId);
        }
      }
    });
  }

  return items;
}
function collectDescendantSlots(itemId: string, slotItems: Record<string, Item>): string[] {
  const item = slotItems[itemId];
  if (!item) return [];

  // If this is a leaf node, and it's a "slot" (has no children):
  if (!item.children || item.children.length === 0) {
    return [itemId.replace(/__file$/, "")];
  }

  // Otherwise recurse
  let slots: string[] = [];
  for (const childId of item.children) {
    slots = slots.concat(collectDescendantSlots(childId, slotItems));
  }
  return slots;
}

const treeIndent = 20

function loadTexture(gl, url) {
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);

    // placeholder pixel until image loads
    const pixel = new Uint8Array([255, 0, 255, 255]); // magenta
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0,
                  gl.RGBA, gl.UNSIGNED_BYTE, pixel);

    const img = new Image();
    img.crossOrigin = ""; // allow CORS if image is from another domain
    img.onload = function () {
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA,
                      gl.RGBA, gl.UNSIGNED_BYTE, img);

        gl.generateMipmap(gl.TEXTURE_2D);
    };
    img.src = url;

    return tex;
}

export default function SpineWebGL({ }: SpineWebGLProps) {
    const id = useId()

    const [files, setFiles] = useState<{
        skel: File | null;
        atlas: File | null;
        png: File | null;
    }>({ skel: null, atlas: null, png: null });

    const [isDragging, setIsDragging] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    const [loadTrigger, setLoadTrigger] = useState(0);

    // 3. Add drag-and-drop handlers:
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const droppedFiles = Array.from(e.dataTransfer.files);
        
        // Categorize files by extension
        const newFiles = {
            skel: droppedFiles.find(f => f.name.endsWith('.skel')) || null,
            atlas: droppedFiles.find(f => f.name.endsWith('.atlas')) || null,
            png: droppedFiles.find(f => f.name.endsWith('.png')) || null,
        };

        // Validate all required files are present
        if (!newFiles.skel || !newFiles.atlas || !newFiles.png) {
            alert('Please drop all 3 files: .skel, .atlas, and .png');
            return;
        }

        setFiles(newFiles);
        setIsLoaded(false);
        setLoadTrigger(prev => prev + 1);  // Trigger reload
    };

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const renderLoopRef = useRef<number | null>(null);

    const mvp = new spine.webgl.Matrix4();

    const stateRef = useRef<spine.AnimationState | null>(null);
    const skeletonRef = useRef<spine.Skeleton | null>(null);

    // Use refs for values that need to be accessed in the render loop
    const isPlayingRef = useRef<boolean>(true);
    const playbackSpeedRef = useRef<number>(1);
    const debugRenderingRef = useRef<boolean>(false);
    const skeletonScaleRef = useRef<number>(1);

    const trackToCenterRef = useRef<boolean>(false);
    const isCapturingRef = useRef<boolean>(false);
    const lastRenderRef = useRef<string>('');

    const skinsListRef = useRef<string[]>([]);
    const currentSkinRef = useRef<string | null>(null);

    const [animations, setAnimations] = useState<string[]>([]);
    const [currentAnim, setCurrentAnim] = useState<string | null>(null);
    const [skeletonScale, setSkeletonScale] = useState<number>(1);
    const [debugRendering, setDebugRendering] = useState<boolean>(false);
    const [isPlaying, setIsPlaying] = useState<boolean>(true);
    const [currentFrame, setCurrentFrame] = useState<number>(0);
    const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);

    const [trackToCenter, settrackToCenter] = useState<boolean>(false);
    const [isCapturing, setisCapturing] = useState<boolean>(false);
    const [lastRender, setLastRender] = useState<string>('');

    const [skinsList, setSkinsList] = useState<string[]>([]);
    const [currentSkin, setCurrentSkin] = useState<string | null>(null);

    // ANIMATION TREE START

    // Add these state variables near the top of your component (after other useState declarations)

    // Track which animations are currently looping on additional tracks
    const [loopingAnimations, setLoopingAnimations] = useState<Set<string>>(new Set());

    // Build animation tree data (flat list structure)
    function buildAnimationTreeData(animations: string[]): Record<string, Item> {
    const items: Record<string, Item> = {
        root: { name: "Animations", children: [] },
    };

    for (const animName of animations) {
        items[animName] = { name: animName };
        items.root.children!.push(animName);
    }

    return items;
    }

    // Add this state for animation tree
    const [animationItems, setAnimationItems] = useState<Record<string, Item>>({});

    // Create tree instance for animations
    const animationTree = useTree<Item>({
        rootItemId: "root",
        indent: treeIndent,
        getItemName: (item) => item.getItemData().name,
        isItemFolder: (item) => (item.getItemData()?.children?.length ?? 0) > 0,
        dataLoader: {
            getItem: (id) => animationItems[id],
            getChildren: (id) => animationItems[id]?.children ?? [],
        },
        canCheckFolders: true,
        features: [syncDataLoaderFeature, selectionFeature, checkboxesFeature, hotkeysCoreFeature],
    });

    // Effect to build animation tree when animations are loaded
    useEffect(() => {
        if (animations.length > 0) {
            const built = buildAnimationTreeData(animations);
            setAnimationItems(built);
        }
    }, [animations]);

    // Effect to rebuild animation tree when items change
    useEffect(() => {
        animationTree.rebuildTree();
        // Check items that are currently looping
        animationTree.setCheckedItems(Array.from(loopingAnimations));
    }, [animationItems, loopingAnimations]);

    // Track management: find next available track index (starting from 1, since 0 is main)
    function getNextAvailableTrack(): number {
        if (!stateRef.current) return 1;
        
        for (let i = 1; i < 10; i++) { // Support up to 10 tracks
            if (!stateRef.current.tracks[i] || !stateRef.current.tracks[i].animation) {
                return i;
            }
        }
        return -1; // No available tracks
    }

    // Get track index for a specific animation
    function getTrackForAnimation(animName: string): number {
        if (!stateRef.current) return -1;
        
        for (let i = 1; i < stateRef.current.tracks.length; i++) {
            const track = stateRef.current.tracks[i];
            if (track && track.animation && track.animation.name === animName) {
                return i;
            }
        }
        return -1;
    }

    // Handle animation loop checkbox changes
    function handleAnimationLoopChange(animName: string, shouldLoop: boolean) {
        if (!stateRef.current) return;
        
        if (shouldLoop) {
            // Start looping this animation on a new track
            const trackIndex = getNextAvailableTrack();
            if (trackIndex === -1) {
                alert('Maximum number of concurrent animations reached');
                return;
            }
            
            stateRef.current.setAnimation(trackIndex, animName, true);
            setLoopingAnimations(prev => new Set(prev).add(animName));
        } else {
            // Stop looping this animation
            const trackIndex = getTrackForAnimation(animName);
            if (trackIndex !== -1) {
                stateRef.current.setEmptyAnimation(trackIndex, 0); // Clear the track
            }
            setLoopingAnimations(prev => {
                const newSet = new Set(prev);
                newSet.delete(animName);
                return newSet;
            });
        }
    }

    // ANIMATION TREE END


    // Update refs when state changes
    useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
    useEffect(() => { playbackSpeedRef.current = playbackSpeed; }, [playbackSpeed]);
    useEffect(() => { debugRenderingRef.current = debugRendering; }, [debugRendering]);
    useEffect(() => { skeletonScaleRef.current = skeletonScale; }, [skeletonScale]);
    
    useEffect(() => { trackToCenterRef.current = trackToCenter; }, [trackToCenter]);
    useEffect(() => { isCapturingRef.current = isCapturing; }, [isCapturing]);
    useEffect(() => { lastRenderRef.current = lastRender; }, [lastRender]);

    useEffect(() => { skinsListRef.current = skinsList; }, [skinsList]);

    
    const [currentMaskTex, setCurrentMaskTex] = useState<WebGLTexture | null>(null);
    const [currentDistTex, setCurrentDistTex] = useState<WebGLTexture | null>(null);
    const [currentSubTex, setCurrentSubTex] = useState<WebGLTexture | null>(null);


    const [slotItems, setSlotItems] = useState<Record<string, Item>>({});

    useEffect(() => {
        if (skeletonRef.current) {
            const slotNames = skeletonRef.current.slots.map((s) => s.data.name);
            const built = buildSlotTreeData(slotNames);
            setSlotItems(built);

            // Save attachments
            skeletonRef.current.slots.forEach((slot) => {
            slotAttachmentRef.current.set(slot.data.name, slot.getAttachment());
            });
        }
    }, [skeletonRef.current]);

    const tree = useTree<Item>({
        rootItemId: "root",
        indent: treeIndent,
        getItemName: (item) => item.getItemData().name,
        isItemFolder: (item) => (item.getItemData()?.children?.length ?? 0) > 0,
        dataLoader: {
            getItem: (id) => slotItems[id],
            getChildren: (id) => slotItems[id]?.children ?? [],
        },
        canCheckFolders: true,
        features: [syncDataLoaderFeature, selectionFeature, checkboxesFeature, hotkeysCoreFeature],
    });


    useEffect(() => {
        tree.rebuildTree()
        tree.setCheckedItems(Object.keys(slotItems))
    }, [slotItems]);

    const slotAttachmentRef = useRef<Map<string, spine.Attachment>>(new Map());

    function recenterSkel(skeleton) {
        skeleton.updateWorldTransform();
        const offset = new spine.Vector2();
        const size = new spine.Vector2();
        skeleton.getBounds(offset, size, []);
        return { offset: offset, size: size };
    }
    
    useEffect(() => {

        if (!canvasRef.current) {
            const timer = setTimeout(() => {
                setLoadTrigger(prev => prev + 1); // Retry
            }, 10);
            return () => clearTimeout(timer);
        }

        const canvas = canvasRef.current;
        
        const config = { alpha: false, preserveDrawingBuffer: true };
        const gl = canvas.getContext("webgl", config) as WebGLRenderingContext;
        if (!gl) {
            alert('WebGL is unavailable.');
            return;
        }

        if (renderLoopRef.current) {
            cancelAnimationFrame(renderLoopRef.current);
            renderLoopRef.current = null;
        }

        if (!canvasRef.current || !files.skel || !files.atlas || !files.png) return;


        let shader;
        shader = spine.webgl.Shader.newTwoColoredTextured(gl);

        const batcher = new spine.webgl.PolygonBatcher(gl);
        mvp.ortho2d(0, 0, canvas.width - 1, canvas.height - 1);

        let bounds: { offset: spine.Vector2; size: spine.Vector2 };
        const skeletonRenderer = new spine.webgl.SkeletonRenderer(gl);
        
        let pngUrl: string; // Declare here so cleanup can access it

        const loadSpine = async () => {
            // Read atlas file as text
            const atlasText = await files.atlas!.text();
            
            // Create object URL for the PNG
            pngUrl = URL.createObjectURL(files.png!);
            
            // Pre-load the image
            const preloadedImage = await new Promise<HTMLImageElement>((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                    console.log('Image loaded successfully');
                    resolve(img);
                };
                img.onerror = (e) => {
                    console.error('Image failed to load:', e);
                    reject(new Error('Failed to load image'));
                };
                img.src = pngUrl;
            });

            // Create atlas
            const atlas = new spine.TextureAtlas(atlasText, (path) => {
                return new spine.webgl.GLTexture(gl, preloadedImage);
            });

            // Load skeleton binary
            const buffer = await files.skel!.arrayBuffer();
            const attachmentLoader = new spine.AtlasAttachmentLoader(atlas);
            const binary = new spine.SkeletonBinary(attachmentLoader);
            binary.scale = 1;
            
            const skeletonData = binary.readSkeletonData(new Uint8Array(buffer));

            // Build skeleton + state
            const skeleton = new spine.Skeleton(skeletonData);
            skeletonRef.current = skeleton;

            const skins = skeleton.data.skins.map(s => s.name);
            setSkinsList(skins);

            skeleton.setSkinByName("default");
            setCurrentSkin("default");

            skeleton.slots.forEach((slot) => {
                slotAttachmentRef.current.set(slot.data.name, slot.getAttachment());
            });

            const anims = skeleton.data.animations.map(a => a.name);
            setAnimations(anims);

            const state = new spine.AnimationState(new spine.AnimationStateData(skeleton.data));
            stateRef.current = state;

            // Pick animation
            const firstAnim = skeleton.data.animations[0]?.name;

            if (firstAnim) {
                stateRef.current.setAnimation(0, firstAnim, true);
                setCurrentAnim(firstAnim);
            }

            const offset = new spine.Vector2();
            const size = new spine.Vector2();
            skeleton.setToSetupPose();
            skeleton.updateWorldTransform();
            skeleton.getBounds(offset, size, []);
            bounds = { offset, size };

            let lastFrameTime = Date.now() / 1000;
            const render = () => {
                const now = Date.now() / 1000;
                const delta = now - lastFrameTime;
                lastFrameTime = now;

                resize();
                
                gl.clearColor(0.3, 0.3, 0.3, 1);
                gl.clear(gl.COLOR_BUFFER_BIT);

                if (stateRef.current && skeletonRef.current) {
                    if (isPlayingRef.current) {
                        stateRef.current.update(delta * playbackSpeedRef.current);
                    }
                    stateRef.current.apply(skeletonRef.current);
                    skeletonRef.current.updateWorldTransform();

                    // Update current frame display
                    const track = stateRef.current.tracks[0];
                    if (track && track.animation) {
                        const frameTime = track.trackTime % track.animation.duration;
                        const newFrame = Math.round(frameTime * 30);
                        if (newFrame !== currentFrame) {
                            setCurrentFrame(newFrame);
                        }
                    }

                    shader.bind();

                    shader.setUniformi(spine.webgl.Shader.SAMPLER, 0);
                    shader.setUniform4x4f(spine.webgl.Shader.MVP_MATRIX, mvp.values);

                    batcher.begin(shader);
                    skeletonRenderer.premultipliedAlpha = false;
                    skeletonRenderer.draw(batcher, skeleton);
                    batcher.end();

                    shader.unbind();
                }

                renderLoopRef.current = requestAnimationFrame(render);
            };

            const resize = () => {
                const w = canvas.clientWidth;
                const h = canvas.clientHeight;
                if (canvas.width != w || canvas.height != h) {
                    canvas.width = w;
                    canvas.height = h;
                }

                if (trackToCenterRef.current) {
                    bounds = recenterSkel(skeleton);
                }

                const centerX = bounds.offset.x + bounds.size.x / 2;
                const centerY = bounds.offset.y + bounds.size.y / 2;
                const scaleX = bounds.size.x / canvas.width;
                const scaleY = bounds.size.y / canvas.height;
                let scale = Math.max(scaleX, scaleY) * 1.2;

                if (scale < 1) scale = 1;
                scale = scale * skeletonScaleRef.current;

                const width = canvas.width * scale;
                const height = canvas.height * scale;

                mvp.ortho2d(centerX - width / 2, centerY - height / 2, width, height);
                gl.viewport(0, 0, canvas.width, canvas.height);
            };

            render(); // Start the render loop
        };

        loadSpine().catch((err) => console.error("Spine load failed:", err));
        
        // ✓ RETURN CLEANUP FROM useEffect
        return () => {
            // Stop render loop
            if (renderLoopRef.current) {
                cancelAnimationFrame(renderLoopRef.current);
                renderLoopRef.current = null;
            }
            // Clean up object URL
            if (pngUrl) {
                URL.revokeObjectURL(pngUrl);
            }
        };
    }, [loadTrigger]);

    useEffect(() => {
        if (skeletonRef.current && stateRef.current && animations.length > 0) {
            setIsLoaded(true);
        }
    }, [animations]);

    const handleExport = () => {
        canvasRef.current.toBlob(blob => {
            try 
            {
                navigator.clipboard.write([
                    new ClipboardItem({ 'image/png': blob }) // Use the same MIME type as in toBlob()
                ]);
                alert(`Copied to clipboard.`);
            } catch (error: unknown) {
                    alert(`Failed to copy image to clipboard:${error}`);
            }
        })
        
    };

    // Handle manual frame setting
    const handleFrameChange = (frame: number) => {
        if (stateRef.current && currentAnim) {
            const track = stateRef.current.tracks[0];
            if (track && track.animation) {
                const frameTime = frame / 30; // Assuming 30 FPS
                track.trackTime = Math.min(frameTime, track.animation.duration);
                setCurrentFrame(frame);
            }
        }
    };
    
    return (
        <div>
        {/* Drop zone - show when not loaded */}
        {!isLoaded && (
            <>
                <h1 className="text-2xl">
                    Spine2D 3.8.XX Viewer
                </h1>
                (Supports animation layering & layer hiding)
                <br/>
                <br/>
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                        isDragging 
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' 
                            : 'border-gray-300 dark:border-gray-700'
                    }`}
                >
                    <div className="text-lg">
                        Drop Spine files here
                    </div>
                    <small>
                        <a className="mb-2 text-blue-400" href="https://github.com/EsotericSoftware/spine-runtimes/tree/3.8/spine-ts/webgl/example/assets">(Official 3.8.99 Examples)</a>
                    </small>
                    <div className="text-sm text-gray-500">
                        Required: .skel, .atlas, and .png files
                    </div>
                    {files.skel && files.atlas && files.png && (
                        <div className="mt-4 text-sm">
                            <div>✓ {files.skel.name}</div>
                            <div>✓ {files.atlas.name}</div>
                            <div>✓ {files.png.name}</div>
                        </div>
                    )}
                </div>
            </>
        )}
        
        {/* Controls and canvas - show when loaded */}
        {isLoaded && (
            <>
                <div className="grid grid-cols-1 [&>*]:justify-center md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 border rounded-t-2xl">
                    {/* Animation Selection */}
                    <div className="flex flex-col">
                        <label className="text-sm font-medium mb-1">Animation</label>
                        <select
                            className="dark:bg-black border p-2 rounded"
                            value={currentAnim || ""}
                            onChange={(e) => {
                                const animName = e.target.value;
                                setCurrentAnim(animName);
                                if (stateRef.current) {
                                    stateRef.current.setAnimation(0, animName, true);
                                }
                            }}
                        >
                            {animations.map((name) => (
                                <option className="dark:text-white" key={name} value={name}>
                                    {name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {
                        skinsListRef.current.length > 1 ?
                        (<div className="flex flex-col">
                        <label className="text-sm font-medium mb-1">Appearance</label>
                        <select
                            className="dark:bg-black border p-2 rounded"
                            value={currentSkin || "default"}
                            onChange={(e) => {
                                const skinName = e.target.value;
                                setCurrentSkin(skinName)
                                if (skeletonRef.current) {
                                    skeletonRef.current.setSkinByName(skinName)
                                    skeletonRef.current.setSlotsToSetupPose()
                                    if (stateRef.current) {
                                        stateRef.current.apply(skeletonRef.current)
                                    }
                                    
                                    const anims = skeletonRef.current.data.animations.map(a => a.name);
                                    console.log(animations)
                                    console.log(anims)
                                }
                            }}
                        >
                            {skinsList.map((name) => (
                                <option className="dark:text-white" key={name} value={name}>
                                    {name}
                                </option>
                            ))}
                        </select>
                    </div>) : null
                    }

                    {/* Skeleton Scale */}
                    <div className="flex flex-col">
                        <label className="text-sm font-medium mb-1">Zoom: {skeletonScale.toFixed(2)}x</label>
                        <input
                            type="range"
                            min="0.25"
                            max="2.5"
                            step="0.1"
                            value={skeletonScale}
                            onChange={(e) => setSkeletonScale(parseFloat(e.target.value))}
                            className="w-full"
                        />
                    </div>

                    {/* Playing Control */}
                    <div className="flex flex-col">
                        <label className="text-sm font-medium mb-1">Playback</label>
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={isPlaying}
                                onChange={(e) => setIsPlaying(e.target.checked)}
                                className="mr-2"
                            />
                            Playing
                        </label>
                    </div>

                    {/* Current Frame */}
                    <div className="flex flex-col">
                        <label className="text-sm font-medium mb-1">Current Frame</label>
                        <input
                            type="number"
                            value={currentFrame}
                            onChange={(e) => handleFrameChange(parseInt(e.target.value) || 0)}
                            className="dark:bg-black border p-2 rounded"
                            min="0"
                        />
                    </div>

                    {/* Playback Speed */}
                    <div className="flex flex-col">
                        <label className="text-sm font-medium mb-1">Speed: {playbackSpeed.toFixed(2)}x</label>
                        <input
                            type="range"
                            min="0.25"
                            max="2.5"
                            step="0.05"
                            value={playbackSpeed}
                            onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
                            className="w-full"
                        />
                    </div>

                    {/* Center Track Control */}
                    <div className="flex flex-col">
                        <label className="text-sm font-medium mb-1">Track to Center</label>
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={trackToCenter}
                                onChange={(e) => settrackToCenter(e.target.checked)}
                                className="mr-2"
                            />
                            Re-center
                        </label>
                    </div>

                    {/* Export Button */}
                    <div className="flex flex-col justify-end">
                        <button className="border p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                            onClick={handleExport}
                        >
                            Copy Image to Clipboard
                        </button>
                    </div>

                    {/* Back Button */}
                    <div className="flex flex-col justify-end">
                        <Link href={"."}>
                        <button className="border p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800">

                                Back
                        </button>
                            </Link>
                    </div>
                </div>
            </>
        )}
        
        {/* Canvas - ALWAYS RENDERED, never conditional */}
        <canvas 
            ref={canvasRef} 
            width={750} 
            height={isLoaded ? 500 : 0} 
            className="border w-full"
            style={isLoaded ? { display: 'block' } : {display: "none"} }
        />
        
        {/* Advanced options - show when loaded */}
        {isLoaded && (
            <div className="flex flex-row [&>*]:justify-center gap-4 p-4 border rounded-b-2xl">
                <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="advancedoptions" className="w-full">
                        <AccordionTrigger className="p-5 text-2xl group-data-[state=open]:block w-full">Advanced Options</AccordionTrigger>
                        <AccordionContent>
                            <div className="flex flex-row gap-4 [&>div]:p-5 [&>div]:border [&>div]:rounded-2xl">
                                <div>
                                    <h2 className="text-xl pb-2">Show / Hide Slots</h2>
                                    <Tree
                                        className="relative before:absolute before:inset-0 before:ms-4.5 before:bg-[repeating-linear-gradient(to_right,transparent_0,transparent_calc(var(--tree-indent)-1px),var(--border)_calc(var(--tree-indent)-1px),var(--border)_calc(var(--tree-indent)))]"
                                        indent={treeIndent}
                                        tree={tree}
                                        >
                                        {tree.getItems().map((item) => {
                                            return (
                                            <div
                                                key={item.getId()}
                                                className="flex items-center gap-1.5 not-last:pb-0.5"
                                            >
                                                <Checkbox
                                                    checked={{
                                                        checked: true,
                                                        unchecked: false,
                                                        indeterminate: "indeterminate" as const,
                                                    }[item.getCheckedState()]}
                                                    onCheckedChange={(checked) => {
                                                        const checkboxProps = item.getCheckboxProps();
                                                        checkboxProps.onChange?.({ target: { checked } });
                    
                                                        if (item.isFolder()) {
                                                            // Collect all descendant slot IDs
                                                            const slotNames = collectDescendantSlots(item.getId(), slotItems);
                    
                                                            slotNames.forEach((slotName) => {
                                                            const slot = skeletonRef.current?.findSlot(slotName);
                                                            if (!slot) return;
                    
                                                            if (checked) {
                                                                const original = slotAttachmentRef.current.get(slotName);
                                                                if (original) slot.setAttachment(original);
                                                            } else {
                                                                slotAttachmentRef.current.set(slotName, slot.getAttachment());
                                                                slot.setAttachment(null);
                                                            }
                                                            });
                                                        } else {
                                                            // Leaf node (slot)
                                                            const slotName = item.getId().replace(/__file$/, "");
                                                            const slot = skeletonRef.current?.findSlot(slotName);
                                                            if (!slot) return;
                    
                                                            if (checked) {
                                                            const original = slotAttachmentRef.current.get(slotName);
                                                            if (original) slot.setAttachment(original);
                                                            } else {
                                                            slotAttachmentRef.current.set(slotName, slot.getAttachment());
                                                            slot.setAttachment(null);
                                                            }
                                                        }
                                                        }}
                                                    />
                                                <TreeItem item={item} className="flex-1 not-last:pb-0">
                                                <TreeItemLabel className="before:bg-background relative before:absolute before:inset-x-0 before:-inset-y-0.5 before:-z-10">
                                                    <span className="flex items-center gap-2">
                                                    {item.isFolder() ? (
                                                        item.isExpanded() ? (
                                                        <FolderOpenIcon className="text-muted-foreground pointer-events-none size-4" />
                                                        ) : (
                                                        <FolderIcon className="text-muted-foreground pointer-events-none size-4" />
                                                        )
                                                    ) : (
                                                        <FileIcon className="text-muted-foreground pointer-events-none size-4" />
                                                    )}
                                                    {item.getItemName()}
                                                    </span>
                                                </TreeItemLabel>
                                                </TreeItem>
                                            </div>
                                            )
                                        })}
                                    </Tree>
                                </div>
                                <div>
                                    <h2 className="text-xl pb-2">Force Loop Animations</h2>
                                    <Tree
                                        className="relative before:absolute before:inset-0 before:ms-4.5 before:bg-[repeating-linear-gradient(to_right,transparent_0,transparent_calc(var(--tree-indent)-1px),var(--border)_calc(var(--tree-indent)-1px),var(--border)_calc(var(--tree-indent)))]"
                                        indent={treeIndent}
                                        tree={animationTree}
                                    >
                                        {animationTree.getItems().map((item) => {
                                            // Skip the root item in rendering
                                            if (item.getId() === "root") return null;
                                            
                                            return (
                                                <div
                                                    key={item.getId()}
                                                    className="flex items-center gap-1.5 not-last:pb-0.5"
                                                >
                                                    <Checkbox
                                                        checked={{
                                                            checked: true,
                                                            unchecked: false,
                                                            indeterminate: "indeterminate" as const,
                                                        }[item.getCheckedState()]}
                                                        onCheckedChange={(checked) => {
                                                            const checkboxProps = item.getCheckboxProps();
                                                            checkboxProps.onChange?.({ target: { checked } });
                                                            
                                                            const animName = item.getId();
                                                            handleAnimationLoopChange(animName, !!checked);
                                                        }}
                                                    />
                                                    <TreeItem item={item} className="flex-1 not-last:pb-0">
                                                        <TreeItemLabel className="before:bg-background relative before:absolute before:inset-x-0 before:-inset-y-0.5 before:-z-10">
                                                            <span className="flex items-center gap-2">
                                                                {item.getItemName()}
                                                            </span>
                                                        </TreeItemLabel>
                                                    </TreeItem>
                                                </div>
                                            );
                                        })}
                                    </Tree>
                                </div>
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>
        )}
    </div>
    );
}